import { useMemo, useRef, useState } from 'react';

import { WarningIcon } from '@change/ds-icons';

import { Text } from '@change/ds-core-ui/next';

import { buttonSelected, buttonStyles } from '../SelectionCard.styles';

import type { AmountInputProps } from './AmountInput.types';
import { GradientBorder } from './GradientBorder';

export const AmountInputCard: React.FC<AmountInputProps> = ({
	currencySymbol,
	currencySymbolPosition,
	selected,
	onChange,
	onFocus,
	error,
	showFractionNumber,
	decimalSeparator,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [focused, setFocused] = useState(false);
	const [raw, setRaw] = useState('');

	const handleActivate = () => {
		if (!focused) {
			setFocused(true);
			onChange(raw);
			onFocus();
			queueMicrotask(() => inputRef.current?.focus());
		}
	};

	const dollarsDisplay = raw === '' ? '' : String(parseInt(raw, 10));
	const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		const digits = e.target.value.replace(/\D/g, '');
		setRaw(digits);
		onChange(digits);
	};
	const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
		if (e.key === '.' || e.key === ',' || e.key === 'Enter') {
			e.preventDefault();
			if (e.key === 'Enter') inputRef.current?.blur();
		}
	};
	const handleBlur: React.FocusEventHandler<HTMLInputElement> = () => {
		setFocused(false);
	};

	const showOther = !focused && raw === '';
	// Dynamic width in ch units; at least 1ch so caret is visible
	const inputWidthCh = Math.max(1, dollarsDisplay.length || 1);

	const applySelectedStyle = useMemo(() => {
		return selected && !error && !focused;
	}, [selected, error, focused]);

	const rootStyle = useMemo(() => {
		const styles = applySelectedStyle ? [...buttonSelected] : [...buttonStyles];
		if (error) {
			styles.push('border-[2px] border-solid !border-stroke-error-strong');
		}
		return styles.join(' ');
	}, [applySelectedStyle, error]);

	return (
		<>
			<GradientBorder selected={applySelectedStyle}>
				<button type="button" onClick={handleActivate} className={`${rootStyle} relative`}>
					<div className="flex w-full items-center">
						{showOther && (
							<Text variant="body" size="medium" fontWeight="bold">
								Other
							</Text>
						)}
						{!showOther && (
							<div className="flex items-baseline">
								{/* TODO: need design for other currency symbol position */}
								{currencySymbolPosition === 'before' && (
									<span className="text-body-medium pr-1 font-bold">{currencySymbol}</span>
								)}
								<input
									ref={inputRef}
									id="amount-input"
									inputMode="numeric"
									pattern="[0-9]*"
									value={dollarsDisplay}
									onChange={handleChange}
									onKeyDown={handleKeyDown}
									onBlur={handleBlur}
									className="text-body-medium caret-typography-default min-w-0 bg-transparent text-right font-bold outline-none"
									// eslint-disable-next-line react/forbid-dom-props
									style={{ width: `${inputWidthCh}ch` }}
								/>
								{showFractionNumber && (
									<Text variant="body" size="medium" fontWeight="bold">
										{decimalSeparator}00
									</Text>
								)}
							</div>
						)}
					</div>
				</button>
			</GradientBorder>
			{error && (
				<div className="flex gap-1">
					<WarningIcon size="small" className="text-icon-error" />
					<Text variant="body" size="small" className="text-typography-error">
						{error}
					</Text>
				</div>
			)}
		</>
	);
};
